import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { useState } from "react";
import ContentWithdrawFromWallet from "./content";
import { WithdrawWallet } from "@/shared/components/common/icons";
import TableWallet from "../table";
import { useQuery } from "@tanstack/react-query";
import { fetchUserBankAccounts, fetchWithdrawAccounts } from "@/api/user/home";
import {
  numberSeparator,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";
import moment from "jalali-moment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { TextField } from "@mui/material";
import CustomButton from "@/shared/components/kit/button";
import CustomSelect from "@/shared/components/common/select";
import ContentSkeleton from "../content.skeleton";
/**
 * WithdrawFromWallet
 *
 * نمایش بخش برداشت از کیف پول کاربر و جدول تراکنش‌ها.
 *
 * ویژگی‌ها:
 * - دریافت و نمایش حساب‌های بانکی کاربر
 * - دریافت و نمایش تراکنش‌های برداشت از کیف پول
 * - قابلیت جستجو و فیلتر تعداد تراکنش‌ها
 * - استفاده از کامپوننت‌های آماده کارت، جدول و استک برای طراحی واکنش‌گرا
 *
 * @returns JSX.Element
 */
// Task Response:

// این کامپوننت به درستی برای نمایش حالت بارگذاری طراحی شده است. چند نکته و پیشنهاد:
// 1. آرایه map برای ردیف‌ها بدون key است؛ بهتر است از index به عنوان key استفاده شود تا React هشدار ندهد:
//    {[...Array(10)].map((_, index) => ( <StackKit key={index} ... /> ))}
// 2. مقادیر Skeleton قابل تنظیم هستند تا با محتوای واقعی هم‌ارتفاع شوند.
// 3. طراحی کاملاً واکنش‌گرا است و از StackKit برای چینش افقی و عمودی استفاده شده.
// 4. در صورت نیاز به mobile-friendly بیشتر، می‌توان padding و width را با breakpoints کنترل کرد.

const WithdrawFromWallet = () => {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  const { data, isFetched, isLoading } = useQuery({
    queryKey: ["withdraw-data", page, size],
    queryFn: () => fetchWithdrawAccounts({ page, size }),
  });

  const [value, setValue] = useState(10);

  const { data: dataUserBankAccounts } = useQuery({
    queryKey: ["userBankAccounts-data"],
    queryFn: () => fetchUserBankAccounts(),
  });

  const dataHeader = [
    { id: "amount", title: "مبلغ(ریال)" },
    { id: "created_at", title: "تاریخ و ساعت" },
    { id: "trackingCode", title: "کدرهگیری تراکنش" },
    { id: "startTracking", title: "مبدا تراکنش" },
    { id: "bankCardTo", title: "مقصد تراکنش" },
    { id: "status", title: "وضعیت تراکنش" },
  ];

  const handleChange = (val: any) => {
    setValue(val);
  };

  // Handle page change from TableWallet
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle items per page change from TableWallet
  const handleItemsPerPageChange = (newSize: number) => {
    setSize(newSize);
    setPage(1); // Reset to first page when changing items per page
  };

  const dataNew = data?.payments?.map((item: any, index: number) => {
    return {
      ...item,
      row: toPersianNumber(index + 1),
      amount: item?.amount && numberSeparator(item?.amount),
      created_at: toPersianNumber(
        `${moment(item.created_at).locale("fa").format("HH:MM")} ${moment(
          item?.created_at,
        )
          .locale("fa")
          .format("YYYY/MM/DD")} `,
      ),
      trackingCode: item?.trackingCode,
      startTracking: "کیف ساعی پی",
      bankCardTo: item?.bankCard?.title,
      status:
        item?.status === "pending" ? (
          <StackKit direction={"row"} gap={2}>
            <AccessTimeIcon /> درحال بررسی
          </StackKit>
        ) : (
          item.status
        ),
    };
  });
  return (
    <StackKit
      direction={"column"}
      sx={{ width: "100%", p: { xs: 1, sm: 2, md: 3 } }}
      gap={2}
    >
      <StackKit
        direction={{ xs: "column", sm: "row" }}
        sx={{
          width: { xs: "100%", sm: "40%" },
          gap: "10px",
          alignItems: "center",
        }}
      >
        <WithdrawWallet width={24} height={24} fill="#082A2E" />
        <TypoGraphyKit sx={{ ml: "0 !important" }} variant="subtitle1">
          برداشت از کیف پول
        </TypoGraphyKit>
      </StackKit>

      <ContentWithdrawFromWallet dataUserBankAccounts={dataUserBankAccounts} />

      <StackKit
        sx={{
          background: "#fff",
          minHeight: { xs: "auto" },
          width: "100%",
          borderRadius: "8px",
          padding: { xs: "10px", sm: "20px 10px" },
        }}
      >
        {isLoading ? (
          <ContentSkeleton />
        ) : (
          <TableWallet
            dataHeader={dataHeader}
            dataTbody={dataNew || []}
            pagination={{
              current_page: page,
              per_page: size,
              total: data?.total || 0,
            }}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </StackKit>
    </StackKit>
  );
};

export default WithdrawFromWallet;
